import React from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt, accentGradient, accentGradientDeep } from "../../../shared/themes/redesignTokens";

interface WizardNavigationProps {
  activeStep: number;
  totalSteps: number;
  isSubmitting: boolean;
  onNext: () => void;
  onBack: () => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const WizardNavigation = ({
  activeStep,
  totalSteps,
  isSubmitting,
  onNext,
  onBack,
  onSaveDraft,
  onSubmit,
  onCancel,
}: WizardNavigationProps) => {
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const isMobile = useMediaQuery("(max-width:768px)");
  const isLastStep = activeStep === totalSteps - 1;
  const isFirstStep = activeStep === 0;

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        left: 0,
        right: 0,
        background: dt.cardBg,
        borderTop: "1px solid #E5E7EB",
        boxShadow: dt.cardShadow,
        px: isMobile ? 2 : 4,
        py: 2,
        zIndex: 10,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          maxWidth: "1000px",
          mx: "auto",
          gap: 2,
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}
      >
        {/* Left side - Back / Cancel */}
        <Box sx={{ display: "flex", gap: 1, order: isMobile ? 2 : 1, flex: isMobile ? "1 1 100%" : "auto" }}>
          {!isFirstStep && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                borderRadius: dt.radiusSm,
                borderColor: "#E5E7EB",
                color: "#6B7280",
                textTransform: "none",
                "&:hover": { borderColor: "#D1D5DB", background: "#F9FAFB" },
                flex: isMobile ? 1 : "auto",
              }}
            >
              {translate("common:buttons.back")}
            </Button>
          )}
          <Button
            variant="text"
            onClick={onCancel}
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 500,
              color: "#9CA3AF",
              textTransform: "none",
              flex: isMobile ? 1 : "auto",
            }}
          >
            {translate("common:buttons.cancel")}
          </Button>
        </Box>

        {/* Right side - Save Draft / Next / Submit */}
        <Box sx={{ display: "flex", gap: 1.5, order: isMobile ? 1 : 2, flex: isMobile ? "1 1 100%" : "auto" }}>
          {activeStep > 0 && (
            <Button
              variant="outlined"
              startIcon={<SaveIcon sx={{ fontSize: "16px !important" }} />}
              onClick={onSaveDraft}
              disabled={isSubmitting}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                borderRadius: dt.radiusSm,
                borderColor: dt.blue,
                color: dt.blue,
                textTransform: "none",
                "&:hover": { borderColor: dt.blueHover, background: "rgba(52, 152, 219, 0.04)" },
                flex: isMobile ? 1 : "auto",
              }}
            >
              {translate("saveAsDraft")}
            </Button>
          )}

          {!isLastStep ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={onNext}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                borderRadius: dt.radiusSm,
                background: dt.blue,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { background: dt.blueHover, boxShadow: "none" },
                flex: isMobile ? 1 : "auto",
                minWidth: "120px",
              }}
            >
              {translate("nextStep")}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<SendIcon sx={{ fontSize: "16px !important" }} />}
              onClick={onSubmit}
              disabled={isSubmitting}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                borderRadius: dt.radiusSm,
                background: accentGradient,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { background: accentGradientDeep, boxShadow: "none" },
                flex: isMobile ? 1 : "auto",
                minWidth: "140px",
              }}
            >
              {isSubmitting ? translate("submitting") : translate("submitOffer")}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WizardNavigation;
